import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Clipboard } from '@capacitor/clipboard';
import { Capacitor } from '@capacitor/core';
import * as Device from 'react-device-detect';

import { useSession } from '@hooks';

interface ShareMenuProps {
  type: 'event' | 'spot' | 'consumer';
  idOrSlug: string;
  disabled?: boolean;
}

function ShareMenu(props: Readonly<ShareMenuProps>) {
  const { type, idOrSlug, disabled } = props;
  const { account } = useSession();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { t } = useTranslation('cta');

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Fonction pour copier l'URL
  const handleCopyLink = async () => {
    try {
      let incentive = '',
        url = 'https://festayre.com';

      if (type === 'spot') {
        incentive = "Regarde ce Spot que j'ai trouvé sur Festayre ! \n";
        url += `/spots/${idOrSlug}`;
      } else if (type === 'event') {
        incentive = "Regarde cet Event que j'ai trouvé sur Festayre ! \n";
        url += `/events/${idOrSlug}`;
      } else if (type === 'consumer') {
        url += `/${idOrSlug}`;
      }

      const textToCopy = incentive + url;

      // 1) Capacitor Clipboard API (for native mobile apps)
      if (Capacitor.isNativePlatform()) {
        await Clipboard.write({ string: textToCopy });
        toast.success(t('Link copied to clipboard', { ns: 'notification' }));
        return;
      }

      // 2) Standard API (works on https + localhost, most browsers)
      if (navigator.clipboard?.writeText) {
        console.log('[---> [2] <---]:', true);
        await navigator.clipboard.writeText(textToCopy);
        toast.success(t('Link copied to clipboard', { ns: 'notification' }));
        return;
      }

      // 3) ClipboardItem fallback (some environments expose this but not writeText reliably)
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard?.write && ClipboardItemCtor) {
        console.log('[---> [3] <---]:', true);
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([textToCopy], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
        toast.success(t('Link copied to clipboard', { ns: 'notification' }));
        return;
      }

      // Last resort: let user manually copy
      toast.info(
        <span style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
          {`Copy this link manually:\n${url}`}
        </span>,
        {
          autoClose: false,
          closeButton: true,
        }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
      console.log(`[{errorMessage}]:`, errorMessage);
      const toastMessage = import.meta.env.DEV
        ? `Error copying link: ${errorMessage}`
        : t('Unable to copy link in clipboard', { ns: 'notification' });
      toast.error(toastMessage);
    } finally {
      handleClose();
    }
  };

  // Function to generate the share link according to the application
  const shareLink = (platform: string) => {
    let incentive = '',
      url = 'https://festayre.com';

    if (type === 'spot') {
      incentive = "Regarde ce Spot que j'ai trouvé sur Festayre ! \n";
      url += `/spots/${idOrSlug}`;
    } else if (type === 'event') {
      incentive = "Regarde cet Event que j'ai trouvé sur Festayre ! \n";
      url += `/events/${idOrSlug}`;
    } else if (type === 'consumer') {
      incentive =
        account?.slug === idOrSlug
          ? `Voilà mon profile @${idOrSlug} sur Festayre ! \n`
          : `Voilà le profile @${idOrSlug} de sur Festayre ! \n`;
      url += `/${idOrSlug}`;
    }

    const encodedUrl = encodeURIComponent(url);
    const text = encodeURIComponent(incentive);

    let link = '';

    switch (platform) {
      case 'whatsapp':
        link = `https://wa.me/?text=${text} ${encodedUrl}`;
        break;
      case 'sms':
        // 'sms:' avec `&body` est recommandé pour iOS et `?body` pour Android
        link = /iPhone|iPad|iPod/i.test(navigator.userAgent)
          ? `sms:&body=${text} ${encodedUrl}`
          : `sms:?body=${text} ${encodedUrl}`;
        break;
    }

    window.location.href = link;
    handleClose();
  };

  return (
    <>
      <IconButton disabled={disabled} onClick={handleOpen} size="small" sx={{ p: 0, m: 0 }}>
        <theme.icons.share fontSize={isMobile ? 'small' : 'medium'} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Stack direction="row" spacing={2} p={2} justifyContent="center">
          <Button onClick={handleCopyLink} size="small" color="inherit">
            <Stack alignItems="center" spacing={1}>
              <theme.icons.copy fontSize={'medium'} />
              <Typography variant="caption" fontSize={{ xs: '.8rem', sm: '1rem' }}>
                {t('link')}
              </Typography>
            </Stack>
          </Button>

          {Device.isMobile && (
            <Button onClick={() => shareLink('sms')} size="small" color="inherit">
              <Stack alignItems="center" spacing={1}>
                <theme.icons.sms fontSize={'medium'} />
                <Typography variant="caption" fontSize={{ xs: '.8rem', sm: '1rem' }}>
                  {t('message')}
                </Typography>
              </Stack>
            </Button>
          )}

          {Device.isMobile && (
            <Button onClick={() => shareLink('whatsapp')} size="small" color="inherit">
              <Stack alignItems="center" spacing={1}>
                <theme.icons.whatsapp fontSize={'medium'} />
                <Typography variant="caption" fontSize={{ xs: '.8rem', sm: '1rem' }}>
                  {t('whatsapp')}
                </Typography>
              </Stack>
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
}

export default ShareMenu;
